import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const AttendanceLogs = () => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [filters, setFilters] = useState({
        emp_code: '',
        device_id: '',
        status: '',
        date_from: '',
        date_to: ''
    });
    const [filterOptions, setFilterOptions] = useState({
        emp_codes: [],
        device_ids: [],
        statuses: []
    });
    const [sortBy, setSortBy] = useState('date_desc');

    const navigate = useNavigate();
    const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000';

    const fetchAttendanceLogs = useCallback(async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('authToken');
            if (!token) {
                setError('No authentication token found');
                setLoading(false);
                return;
            }

            const params = {};
            if (filters.emp_code) params.emp_code = filters.emp_code;
            if (filters.device_id) params.device_id = filters.device_id;
            if (filters.status) params.status = filters.status;
            if (filters.date_from) params.date_from = filters.date_from;
            if (filters.date_to) params.date_to = filters.date_to;
            if (searchTerm) params.search = searchTerm;

            const response = await axios.get(`${API_BASE}pushattendance/get_attendance_logs.php`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                params: params,
                timeout: 10000
            });

            if (response.data && response.data.success) {
                setLogs(response.data.logs || []);
                setFilterOptions({
                    emp_codes: response.data.filters?.emp_codes || [],
                    device_ids: response.data.filters?.device_ids || [],
                    statuses: response.data.filters?.statuses || []
                });
                setError('');
            } else {
                setLogs([]);
                setError(response.data?.error || 'Failed to fetch attendance logs');
            }
        } catch (error) {
            setLogs([]);
            const errorMsg = error.response?.data?.error ||
                error.message ||
                'Failed to fetch attendance logs';
            setError(errorMsg);
            console.error('Error fetching attendance logs:', error);
        } finally {
            setLoading(false);
        }
    }, [API_BASE, filters, searchTerm]);

    useEffect(() => {
        fetchAttendanceLogs();
    }, [fetchAttendanceLogs]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleClearFilters = () => {
        setFilters({
            emp_code: '',
            device_id: '',
            status: '',
            date_from: '',
            date_to: ''
        });
        setSearchTerm('');
    };

    const sortedLogs = [...logs].sort((a, b) => {
        const dateA = new Date(a.punch_time);
        const dateB = new Date(b.punch_time);

        switch (sortBy) {
            case 'date_desc':
                return dateB - dateA;
            case 'date_asc':
                return dateA - dateB;
            case 'emp_code':
                return (a.emp_code || '').localeCompare(b.emp_code || '');
            case 'device_id':
                return (a.device_id || 0) - (b.device_id || 0);
            default:
                return dateB - dateA;
        }
    });

    const getStatusBadge = (status) => {
        const badgeClasses = {
            'IN': 'bg-success',
            'OUT': 'bg-danger',
            'CHECKIN': 'bg-primary',
            'CHECKOUT': 'bg-warning',
            'BREAK_START': 'bg-info',
            'BREAK_END': 'bg-secondary'
        };
        return badgeClasses[status] || 'bg-dark';
    };

    const formatDateTime = (dateTime) => {
        if (!dateTime) return 'N/A';
        const date = new Date(dateTime);
        return date.toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    };

    if (loading) {
        return (
            <div className="container-fluid py-4">
                <div className="row">
                    <div className="col-12">
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-3 text-muted">Loading attendance logs...</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid py-4">
            {/* Header */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="card shadow-sm border-0">
                        <div className="card-body py-4">
                            <div className="row align-items-center">
                                <div className="col-md-6">
                                    <div className="d-flex align-items-center">
                                        <div className="btn-group me-3" role="group">
                                            <button
                                                className="btn btn-outline-secondary btn-sm"
                                                onClick={() => navigate('/dashboard')}
                                                title="Go to Dashboard"
                                            >
                                                <i className="bi bi-speedometer2"></i>
                                            </button>
                                            <button
                                                className="btn btn-outline-secondary btn-sm"
                                                onClick={() => navigate('/Menu')}
                                                title="Go to Menu"
                                            >
                                                <i className="bi bi-menu-button"></i>
                                            </button>
                                        </div>
                                        <div>
                                            <h2 className="h4 mb-1 fw-bold text-dark">
                                                <i className="bi bi-clock-history me-2 text-primary"></i>
                                                Attendance Logs
                                                <span className="badge bg-info ms-2">Real-time</span>
                                            </h2>
                                            <p className="text-muted mb-0">
                                                View and filter attendance punch records from biometric devices
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-6 text-end">
                                    <div className="d-flex justify-content-end align-items-center">
                                        <button
                                            className="btn btn-outline-secondary me-2"
                                            onClick={() => navigate('/admin-panel')}
                                        >
                                            <i className="bi bi-arrow-left me-2"></i>
                                            Back to Dashboard
                                        </button>
                                        <button
                                            className="btn btn-primary"
                                            onClick={fetchAttendanceLogs}
                                        >
                                            <i className="bi bi-arrow-clockwise me-2"></i>
                                            Refresh
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Quick Navigation */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="card bg-light border-0">
                        <div className="card-body py-3">
                            <div className="row text-center">
                                <div className="col-md-2 mb-2 mb-md-0">
                                    <button
                                        className="btn btn-outline-primary w-100 d-flex align-items-center justify-content-center"
                                        onClick={() => navigate('/admin-panel')}
                                    >
                                        <i className="bi bi-speedometer2 me-2"></i>
                                        Dashboard
                                    </button>
                                </div>
                                <div className="col-md-2 mb-2 mb-md-0">
                                    <button
                                        className="btn btn-outline-success w-100 d-flex align-items-center justify-content-center"
                                        onClick={() => navigate('/employees')}
                                    >
                                        <i className="bi bi-people me-2"></i>
                                        Employees
                                    </button>
                                </div>
                                <div className="col-md-2 mb-2 mb-md-0">
                                    <button
                                        className="btn btn-outline-info w-100 d-flex align-items-center justify-content-center"
                                        onClick={() => navigate('/attendance')}
                                    >
                                        <i className="bi bi-calendar-check me-2"></i>
                                        Attendance
                                    </button>
                                </div>
                                <div className="col-md-2 mb-2 mb-md-0">
                                    <button
                                        className="btn btn-primary text-white w-100 d-flex align-items-center justify-content-center"
                                        disabled
                                    >
                                        <i className="bi bi-clock-history me-2"></i>
                                        Attendance Logs
                                    </button>
                                </div>
                                <div className="col-md-2 mb-2 mb-md-0">
                                    <button
                                        className="btn btn-outline-warning w-100 d-flex align-items-center justify-content-center"
                                        onClick={() => navigate('/shifts')}
                                    >
                                        <i className="bi bi-clock me-2"></i>
                                        Shifts
                                    </button>
                                </div>
                                <div className="col-md-2 mb-2 mb-md-0">
                                    <button
                                        className="btn btn-outline-dark w-100 d-flex align-items-center justify-content-center"
                                        onClick={() => navigate('/Menu')}
                                    >
                                        <i className="bi bi-menu-button me-2"></i>
                                        Menu
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Error Alert */}
            {error && (
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="alert alert-danger d-flex align-items-center" role="alert">
                            <i className="bi bi-exclamation-triangle-fill me-2"></i>
                            <div>
                                <strong>Error:</strong> {error}
                                <div className="mt-2">
                                    <button
                                        className="btn btn-sm btn-outline-danger me-2"
                                        onClick={fetchAttendanceLogs}
                                    >
                                        <i className="bi bi-arrow-clockwise me-1"></i>
                                        Retry
                                    </button>
                                    <button
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={() => setError('')}
                                    >
                                        Dismiss
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Filters */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="card shadow-sm border-0">
                        <div className="card-body">
                            <div className="row g-3 align-items-center">
                                <div className="col-md-2">
                                    <div className="input-group">
                                        <span className="input-group-text bg-light border-end-0">
                                            <i className="bi bi-search text-muted"></i>
                                        </span>
                                        <input
                                            type="text"
                                            className="form-control border-start-0"
                                            placeholder="Search logs..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                        {searchTerm && (
                                            <button
                                                className="btn btn-outline-secondary border-start-0"
                                                onClick={() => setSearchTerm('')}
                                            >
                                                <i className="bi bi-x"></i>
                                            </button>
                                        )}
                                    </div>
                                </div>

                                <div className="col-md-2">
                                    <select
                                        className="form-select"
                                        name="emp_code"
                                        value={filters.emp_code}
                                        onChange={handleFilterChange}
                                    >
                                        <option value="">All Employees</option>
                                        {filterOptions.emp_codes.map(code => (
                                            <option key={code} value={code}>{code}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="col-md-2">
                                    <select
                                        className="form-select"
                                        name="device_id"
                                        value={filters.device_id}
                                        onChange={handleFilterChange}
                                    >
                                        <option value="">All Devices</option>
                                        {filterOptions.device_ids.map(deviceId => (
                                            <option key={deviceId} value={deviceId}>Device {deviceId}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="col-md-2">
                                    <select
                                        className="form-select"
                                        name="status"
                                        value={filters.status}
                                        onChange={handleFilterChange}
                                    >
                                        <option value="">All Statuses</option>
                                        {filterOptions.statuses.map(status => (
                                            <option key={status} value={status}>{status}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="col-md-2">
                                    <input
                                        type="date"
                                        className="form-control"
                                        name="date_from"
                                        value={filters.date_from}
                                        onChange={handleFilterChange}
                                        placeholder="From Date"
                                    />
                                </div>

                                <div className="col-md-2">
                                    <div className="d-flex justify-content-end">
                                        <button
                                            className="btn btn-outline-secondary me-2"
                                            onClick={handleClearFilters}
                                            title="Clear Filters"
                                        >
                                            <i className="bi bi-x-circle"></i>
                                        </button>
                                        <button
                                            className="btn btn-primary"
                                            onClick={fetchAttendanceLogs}
                                            title="Apply Filters"
                                        >
                                            <i className="bi bi-funnel"></i>
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Date Range Filter */}
                            <div className="row g-3 mt-2">
                                <div className="col-md-2">
                                    <label className="form-label small text-muted">Date Range</label>
                                </div>
                                <div className="col-md-2">
                                    <input
                                        type="date"
                                        className="form-control"
                                        name="date_from"
                                        value={filters.date_from}
                                        onChange={handleFilterChange}
                                        placeholder="From Date"
                                    />
                                </div>
                                <div className="col-md-2">
                                    <input
                                        type="date"
                                        className="form-control"
                                        name="date_to"
                                        value={filters.date_to}
                                        onChange={handleFilterChange}
                                        placeholder="To Date"
                                    />
                                </div>
                                <div className="col-md-6">
                                    <select
                                        className="form-select"
                                        value={sortBy}
                                        onChange={(e) => setSortBy(e.target.value)}
                                    >
                                        <option value="date_desc">Latest First</option>
                                        <option value="date_asc">Oldest First</option>
                                        <option value="emp_code">By Employee Code</option>
                                        <option value="device_id">By Device ID</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Logs Table */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="card shadow-sm border-0">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h5 className="mb-0">
                                    <i className="bi bi-list-ul me-2"></i>
                                    Attendance Logs
                                    <span className="badge bg-primary ms-2">{sortedLogs.length} records</span>
                                </h5>
                                <div className="d-flex align-items-center">
                                    <span className="text-muted me-3">
                                        Last updated: {new Date().toLocaleTimeString()}
                                    </span>
                                    <button
                                        className="btn btn-outline-primary btn-sm"
                                        onClick={fetchAttendanceLogs}
                                    >
                                        <i className="bi bi-arrow-clockwise"></i>
                                    </button>
                                </div>
                            </div>

                            {sortedLogs.length === 0 ? (
                                <div className="text-center py-5">
                                    <i className="bi bi-clock-history display-1 text-muted"></i>
                                    <h4 className="mt-3 text-muted">No attendance logs found</h4>
                                    <p className="text-muted">
                                        {logs.length === 0
                                            ? "No attendance logs recorded yet."
                                            : "No logs match your search criteria."
                                        }
                                    </p>
                                    <button
                                        className="btn btn-primary mt-2"
                                        onClick={fetchAttendanceLogs}
                                    >
                                        <i className="bi bi-arrow-clockwise me-2"></i>
                                        Refresh Data
                                    </button>
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-hover align-middle mb-0">
                                        <thead className="bg-light">
                                            <tr>
                                                <th className="ps-4">Log ID</th>
                                                <th>Employee Code</th>
                                                <th>Punch Time</th>
                                                <th>Device ID</th>
                                                <th>Status</th>
                                                <th className="text-end pe-4">Created At</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {sortedLogs.map((log) => (
                                                <tr key={log.id}>
                                                    <td className="ps-4">
                                                        <div className="fw-semibold">#{log.id}</div>
                                                        <small className="text-muted">Company: {log.company_id}</small>
                                                    </td>
                                                    <td>
                                                        <div className="fw-semibold">
                                                            {log.emp_code || 'N/A'}
                                                        </div>
                                                        <button
                                                            className="btn btn-sm btn-outline-primary mt-1"
                                                            onClick={() => {
                                                                if (log.emp_code) {
                                                                    navigate(`/employee-details?code=${log.emp_code}`);
                                                                }
                                                            }}
                                                            disabled={!log.emp_code}
                                                        >
                                                            <i className="bi bi-person me-1"></i>
                                                            View Employee
                                                        </button>
                                                    </td>
                                                    <td>
                                                        <div className="fw-semibold">
                                                            {formatDateTime(log.punch_time)}
                                                        </div>
                                                        {log.punch_time && (
                                                            <small className="text-muted">
                                                                {new Date(log.punch_time).toLocaleDateString('en-US', { weekday: 'long' })}
                                                            </small>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <span className="badge bg-secondary">
                                                            <i className="bi bi-hdd me-1"></i>
                                                            Device {log.device_id || 'N/A'}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span className={`badge ${getStatusBadge(log.status)}`}>
                                                            <i className="bi bi-record-circle me-1"></i>
                                                            {log.status || 'UNKNOWN'}
                                                        </span>
                                                    </td>
                                                    <td className="text-end pe-4">
                                                        <small className="text-muted">
                                                            {formatDateTime(log.created_at)}
                                                        </small>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Stats Footer */}
            <div className="row mt-4">
                <div className="col-12">
                    <div className="card bg-light border-0">
                        <div className="card-body py-3">
                            <div className="row text-center">
                                <div className="col-md-3">
                                    <div className="fw-bold text-primary fs-4">{logs.length}</div>
                                    <small className="text-muted">Total Logs</small>
                                </div>
                                <div className="col-md-3">
                                    <div className="fw-bold text-success fs-4">
                                        {logs.filter(log => log.status === 'IN' || log.status === 'CHECKIN').length}
                                    </div>
                                    <small className="text-muted">Check-ins</small>
                                </div>
                                <div className="col-md-3">
                                    <div className="fw-bold text-danger fs-4">
                                        {logs.filter(log => log.status === 'OUT' || log.status === 'CHECKOUT').length}
                                    </div>
                                    <small className="text-muted">Check-outs</small>
                                </div>
                                <div className="col-md-3">
                                    <div className="fw-bold text-info fs-4">
                                        {filterOptions.emp_codes.length}
                                    </div>
                                    <small className="text-muted">Unique Employees</small>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Quick Actions Footer */}
            <div className="row mt-4">
                <div className="col-12">
                    <div className="card border-0">
                        <div className="card-body text-center py-3">
                            <div className="btn-group" role="group">
                                <button
                                    className="btn btn-outline-primary"
                                    onClick={fetchAttendanceLogs}
                                >
                                    <i className="bi bi-arrow-clockwise me-2"></i>
                                    Refresh Logs
                                </button>
                                <button
                                    className="btn btn-outline-success"
                                    onClick={() => navigate('/employees')}
                                >
                                    <i className="bi bi-people me-2"></i>
                                    Manage Employees
                                </button>
                                <button
                                    className="btn btn-outline-info"
                                    onClick={() => navigate('/attendance')}
                                >
                                    <i className="bi bi-calendar-check me-2"></i>
                                    View Attendance
                                </button>
                                <button
                                    className="btn btn-outline-warning"
                                    onClick={() => navigate('/shifts')}
                                >
                                    <i className="bi bi-clock me-2"></i>
                                    Manage Shifts
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AttendanceLogs;